import type { ComponentProps } from 'react'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import { useAppDispatch } from 'hooks/reduxHooks'
import { addArrivalDetails, addToCheckoutCart } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import OfferCheckoutButton from './OfferCheckoutButton'
import { isFlightItem, isVillaItem } from 'lib/checkout/checkoutUtils'
import type { SetOptional } from 'type-fest'
import { addToCart } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import type TextButton from 'LuxKit/Button/TextButton'
import useInvalidateStalePrefetchedFlightsFromOffer from 'components/OfferPage/hooks/prefetchFlights/useInvalidateStalePrefetchedFlightsFromOffer'

// Props we want to expose from the OfferCheckoutButton
type OfferCheckoutButtonProps = Pick<ComponentProps<typeof OfferCheckoutButton>,
  'packageView'
  | 'overrideRooms'
  | 'onSubmit'
  | 'upgradeEnabled'
>
// We generally want all TextButton props available, though the signature for onSubmit should come from CheckoutButtonProps
type TextButtonProps = SetOptional<Omit<ComponentProps<typeof TextButton>, 'onSubmit'>, 'kind'>

interface Props extends OfferCheckoutButtonProps, TextButtonProps {
  addToCartAndKeepBrowsing?: boolean
  checkoutSource?: App.CheckoutSource
}

function OfferBookButton(props: Props) {
  const {
    children,
    packageView,
    addToCartAndKeepBrowsing,
    checkoutSource,
    onSubmit,
    kind = 'primary',
    overrideRooms,
    ...rest
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const invalidateStalePrefetchedFlightsFromOffer = useInvalidateStalePrefetchedFlightsFromOffer()

  const addedToCartMessage = intl.formatMessage({
    id: 'mWWOvyoc2jwWyJn4U8Uu',
    defaultMessage: 'Your item has been added to the cart.',
  })
  const viewCartLabel = intl.formatMessage({
    id: '7au9gwzUVBBIuuLo5ocg',
    defaultMessage: 'View cart',
  })

  const onProceedToBooking = useCallback((items: Array<App.Checkout.AnyItem>) => {
    const villaItems = items.filter(isVillaItem)
    const flights = items.some(isFlightItem)
    if (!addToCartAndKeepBrowsing) {
      invalidateStalePrefetchedFlightsFromOffer()
    }

    if (villaItems.length > 0) {
      villaItems.forEach(item => {
        dispatch(addArrivalDetails(item.itemId, { arrivalDate: item.checkIn, arrivalTime: '', arrivalFlightNumber: '' }))
      })
    }

    dispatch(addToCheckoutCart(items, checkoutSource ? { checkoutSource } : undefined))

    if (addToCartAndKeepBrowsing) {
      showSnackbar(addedToCartMessage, 'success', { action: { label: viewCartLabel, to: '/cart' } })
      return
    }

    if (flights) {
      dispatch(navigateToCheckout({ skipSpoofedModal: true }))
    } else {
      dispatch(navigateToCheckout())
    }
    trackEvent(addToCart())
  }, [addToCartAndKeepBrowsing, checkoutSource, dispatch, addedToCartMessage, invalidateStalePrefetchedFlightsFromOffer, viewCartLabel])

  return (
    <OfferCheckoutButton
      {...rest}
      kind={kind}
      onSubmit={onSubmit}
      overrideRooms={overrideRooms}
      packageView={packageView}
      onProceedToBooking={onProceedToBooking}
    >
      {children}
    </OfferCheckoutButton>
  )
}

export default OfferBookButton
